import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React from 'react';

import Text from './oc-text';
import Theme from '../../themes/index';

/*
  Paragraph components are named for their visual appearance,
  * and will default to using the same tag for their actual HTML output.
  * tag: p - allows override of HTML tag used in output
  * font: [body, heading] - specifies whether body or heading font should be used
*/

const generateInternalComponent = defaultTag => {
  // eslint-disable-next-line react/prop-types
  const component = React.forwardRef(({ tag = defaultTag, id, className, prehead, children }, ref) => {
    const Tag = tag;
    return (
      <Tag id={id} ref={ref} className={className}>
        {prehead && (
          <Prehead className="prehead">
            <Text content={prehead} />
          </Prehead>
        )}
        {children}
      </Tag>
    );
  });
  component.displayName = `Internal${defaultTag.toUpperCase()}`;
  return component;
};

const preheadMobile = props => css`
  font-size: ${props.font === 'body' ? '12px' : '14px'};
  line-height: ${props.font === 'body' ? '14px' : '16px'};
  text-transform: ${props.font === 'body' ? 'none' : 'uppercase'};
`;

const preheadDesktop = props => css`
  font-size: ${props.font === 'body' ? '14px' : '16px'};
  line-height: ${props.font === 'body' ? '16px' : '18px'};
  text-transform: ${props.font === 'body' ? 'none' : 'uppercase'};
`;

const Prehead = styled.span`
  color: #333;
  font-weight: normal;
  font-family: ${Theme.fonts.body};
  margin-bottom: 4px;
  letter-spacing: 0;

  .background-black & {
    color: white;
  }
`;

const StyledParagraph = styled(generateInternalComponent('p'))`
  // This selector exists solely to prevent overrides from global styles in headings.scss
  html body & {
    font-family: ${props => (props.font === 'body' ? Theme.fonts.body : Theme.fonts.heading)};
    font-weight: ${props => (props.font === 'body' ? 700 : 500)};
    text-transform: ${props => (props.font === 'body' ? 'none' : Theme.text.headingTextTransform)};
    line-height: ${props => (props.font === 'body' ? '24px' : '26px')};
    font-size: 24px;

    ${Prehead} {
      ${preheadMobile}
    }

    @media (min-width: 1200px) {
      line-height: ${props => (props.font === 'body' ? '28px' : '30px')};
      font-size: 28px;

      ${Prehead} {
        ${preheadDesktop}
      }
    }
  }

  html:not([lang^='en']) & {
    line-height: ${props => (props.font === 'body' ? '22px' : '30px')};

    @media (min-width: 1200px) {
      font-size: ${props => (props.font === 'body' ? '21px' : '26px')};
      line-height: ${props => (props.font === 'body' ? '23px' : '30px')};
    }
  }

  .background-black & {
    color: white;
  }
`;

const Paragraph = ({ prehead, font, tag, children }) => (
  <StyledParagraph prehead={prehead} font={font} tag={tag}>
    {children}
  </StyledParagraph>
);
Paragraph.displayName = 'Paragraph';

Paragraph.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  prehead: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  font: PropTypes.oneOf(['body', 'heading']),
  tag: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Paragraph.defaultProps = {
  prehead: undefined,
  font: 'body',
  tag: 'p',
};

export default Paragraph;
